import { Notification, Typography } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import { DropdownField } from "apps/oneOnboarding/form/DropdownField";
import { useOnboardingPageContextAtomValue } from "apps/oneOnboarding/onboardingPageContextAtom";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import type { ChannelManagerDropdownItem } from "libs/codegen/property-onboarding";
import { useFormContext, useWatch } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";
import { PropertyPricingList } from "./PropertyPricingList";

interface Props {
    triggerChangeOnAutoSelect?: boolean;
}

export const BatchChannelManagerPriceController = ({ triggerChangeOnAutoSelect = false }: Props) => {
    const {
        cmsData: {
            pricing: {
                channelManagerSetup,
                channelManager,
                pleaseSelectAChannelManagerToContinue,
                howToSetUpYourChannelManager,
                instructionsForSettingUpYourChannelManager,
            },
        },
    } = useStaticDataAtomValue();
    const { channelManagers: cmList } = useOnboardingPageContextAtomValue();

    const { control, setValue } = useFormContext<BulkPropertyFormData>();
    const properties = useWatch<BulkPropertyFormData>({
        control,
        name: "propertyDataList",
    }) as BulkPropertyFormData["propertyDataList"];

    const handleSetChannelManager = (channelManager: ChannelManagerDropdownItem) => {
        properties.forEach((_, i) => {
            setValue(`propertyDataList.${i}.channelManager`, channelManager);
        });
    };

    return (
        <VStack gap="16" data-testid="batch-channel-manager-price-controller">
            <Typography variant="heading-default">{channelManagerSetup}</Typography>
            <DropdownField<BulkPropertyFormData, ChannelManagerDropdownItem>
                label={channelManager}
                field="propertyDataList.0.channelManager"
                onChange={handleSetChannelManager}
                triggerChangeOnAutoSelect={triggerChangeOnAutoSelect}
                items={cmList ?? []}
                identifyBy="userId"
                rules={{
                    required: pleaseSelectAChannelManagerToContinue,
                }}
                dataElementName="channel-manager-dropdown"
                data-testid="channel-manager-dropdown"
            />
            <Notification
                intent="notice"
                title={howToSetUpYourChannelManager}
                description={instructionsForSettingUpYourChannelManager}
                asset={{
                    id: "fill.symbol.circle-info",
                }}
                data-testid="channel-manager-setup-instructions"
            />
            <PropertyPricingList hideHeader />
        </VStack>
    );
};
